// Extracted from web/App.tsx; behavior and declaration bodies preserved.
import { useEffect, useState } from "react";

import { GitBranch } from "lucide-react";
import type { ModelDocument, Operation } from "../../shared/contracts";

import { vocabulary, relationshipSuggestion, RELATIONSHIP_SUGGESTION_VERSION } from "../../shared/entityPresentation";

import { uid } from "../../core";
import { Button, Field, Modal, Notice, errorText } from "../components/Primitives";

export function RelationshipDialog({
  document,
  sourceId,
  targetId,
  representationId,
  diagramId,
  onClose,
  onCreate,
}: {
  document: ModelDocument;
  diagramId?: string;
  sourceId?: string;
  targetId?: string;
  representationId: string;
  onClose: () => void;
  onCreate: (ops: Operation[], id: string) => void;
}) {
  const roots = document.objects.filter(
    (o) => !o.ownerObjectId && o.representationId === representationId,
  );
  const [source, setSource] = useState(
    roots.some((o) => o.objectId === sourceId)
      ? sourceId!
      : roots[0]?.objectId || "",
  );
  const [target, setTarget] = useState(
    roots.some(o => o.objectId === targetId) ? targetId! : roots.find((o) => o.objectId !== source)?.objectId || roots[0]?.objectId || "",
  );
  const suggestion = relationshipSuggestion(document, representationId, source, target);
  const [name, setName] = useState(suggestion.name);
  const [description, setDescription] = useState(suggestion.description);
  const [nameEdited, setNameEdited] = useState(false), [descriptionEdited, setDescriptionEdited] = useState(false);
  const terms = vocabulary(document, representationId, undefined, document.diagrams.find(d=>d.diagramId===diagramId)?.perspective);
  useEffect(() => { if (!nameEdited) setName(suggestion.name); if (!descriptionEdited) setDescription(suggestion.description); }, [suggestion.name, suggestion.description, nameEdited, descriptionEdited]);
  const [selfReference,setSelfReference]=useState(false);
  const [sourceOptional,setSourceOptional]=useState(false);
  const [labelSource,setLabelSource]=useState<"name"|"description">("name");
  const [error,setError]=useState("");
  const collision=document.relationships.some(r=>r.representationId===representationId&&r.name.normalize("NFKC").toLowerCase()===name.trim().normalize("NFKC").toLowerCase());
  const endpointsValid=roots.some(o=>o.objectId===source)&&roots.some(o=>o.objectId===target);
  const valid=endpointsValid&&!!name.trim()&&name.trim().length<=512&&!collision&&(source!==target||selfReference);
  const submit=()=>{
    setError("");
    if(!valid){setError("Review both endpoints, the relationship name and any self-reference before adding.");return;}
    const id=uid();
    const operations:Operation[]=[{type:"relationship.create",relationship:{relationshipId:id,name:name.trim(),description:description.trim(),representationId,sourceObjectId:source,targetObjectId:target,sourceCardinality:cardinality.split("-")[0] as "one"|"many",targetCardinality:cardinality.split("-")[1] as "one"|"many",sourceOptional,targetOptional:optional,identifying,extensions:{suggestionProvider:RELATIONSHIP_SUGGESTION_VERSION}}}];
    if(diagramId&&labelSource!=="name")operations.push({type:"connector.set",diagramId,connector:{relationshipId:id,style:{label:{source:labelSource}}}});
    try{onCreate(operations,id);}catch(e){setError(errorText(e));}
  };
  const [cardinality, setCardinality] = useState("one-many");
  const [optional, setOptional] = useState(false);
  const [identifying, setIdentifying] = useState(false);
  return (
    <Modal
      title={`Connect two ${terms.objects}`}
      subtitle="Review source and target roles, cardinality and participation. Click order does not establish business parenthood."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            icon={GitBranch}
            variant="primary"
            disabled={!valid}
            onClick={submit}
          >
            Add relationship
          </Button>
        </>
      }
    >
      <div className="form-row">
        <Field label={`Source ${terms.object}`}>
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            {roots.map((o) => (
              <option key={o.objectId} value={o.objectId}>
                {o.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={`Target ${terms.object}`}>
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            {roots.map((o) => (
              <option key={o.objectId} value={o.objectId}>
                {o.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Relationship name">
        <input
          value={name}
          onChange={(e) => { setName(e.target.value); setNameEdited(true); }}
          maxLength={512}
          placeholder="e.g. Customer_Order"
        />
        <div className="relationship-field-actions"><small>{nameEdited?"Your name":"Suggested name"}</small><Button onClick={()=>{setNameEdited(false);setName(suggestion.name);}}>Use suggested name</Button></div>
      </Field>
      <Field label="Relationship description" hint="Suggested from the endpoints. Refine its business meaning; the caption can use this description.">
        <textarea value={description} onChange={e => { setDescription(e.target.value); setDescriptionEdited(true); }} rows={2} />
        <div className="relationship-field-actions"><small>{descriptionEdited?"Your description":"Suggested description"}</small><Button onClick={()=>{setDescriptionEdited(false);setDescription(suggestion.description);}}>Use suggested description</Button></div>
      </Field>
      {collision&&<Notice kind="warning">This relationship name is already used. Your input is retained. Use suggested name to choose {suggestion.name} or enter another name.</Notice>}
      {error&&<Notice kind="error">{error}</Notice>}
      <div className="form-row"><Button onClick={() => { setSource(target); setTarget(source); }}>Swap endpoints</Button></div>
      {source===target&&<label className="check-row"><input type="checkbox" checked={selfReference} onChange={e=>setSelfReference(e.target.checked)}/>Create an intentional self-reference on this {terms.object}</label>}
      <Field label="Relationship label source"><select value={labelSource} onChange={e=>setLabelSource(e.target.value as "name"|"description")}><option value="name">Name</option><option value="description">Description</option></select><div className="relationship-label-preview" aria-label="Relationship label preview">{labelSource==="description"&&description.trim()?description.trim():name.trim()||"Enter a relationship name"}</div>{labelSource==="description"&&!description.trim()&&<small>Empty description falls back to the name.</small>}</Field>
      <Field label="Cardinality">
        <select
          value={cardinality}
          onChange={(e) => setCardinality(e.target.value)}
        >
          <option value="one-many">One to many</option>
          <option value="one-one">One to one</option>
          <option value="many-many">Many to many</option>
          <option value="many-one">Many to one</option>
        </select>
      </Field>
      <label className="check-row">
        <input
          type="checkbox"
          checked={optional}
          onChange={(e) => setOptional(e.target.checked)}
        />
        Target participation is optional
      </label>
      <label className="check-row"><input type="checkbox" checked={sourceOptional} onChange={e=>setSourceOptional(e.target.checked)}/>Source participation is optional</label>
      <label className="check-row">
        <input
          type="checkbox"
          checked={identifying}
          onChange={(e) => setIdentifying(e.target.checked)}
        />
        Identifying relationship
      </label>
      <Notice>
        After creation, review the field mapping in relationship properties
        before generating physical constraints.
      </Notice>
    </Modal>
  );
}
